import type {
  DetailRow,
  ReadableBlock,
  ToolPresentation,
} from './toolPresenters';

// Readable views of the email-setup diagnostics tools (crm-chat-api
// tools/dns.ts and tools/mail-setup.ts): an MX/record table for dns_lookup, a
// settings card with copy buttons for mail_settings_detect, and a pass/fail
// list for mail_server_probe. Built from the JSON output only.

export const MAIL_TOOL_NAMES = [
  'dns_lookup',
  'mail_settings_detect',
  'mail_server_probe',
] as const;

type NylasOption = 'google' | 'microsoft' | 'icloud' | 'yahoo' | 'imap';
type Security = 'ssl' | 'starttls' | 'none';
type ProbeCheckId = 'dns' | 'connect' | 'tls' | 'greeting' | 'login_methods';

export type MailPresenterLabels = {
  names: string;
  recordType: string;
  domain: string;
  priority: string;
  mailServer: string;
  value: string;
  noMail: string;
  implicitMx: string;
  emailOrDomain: string;
  nylasOption: string;
  provider: string;
  mailFilter: string;
  imapServer: string;
  smtpServer: string;
  port: string;
  security: string;
  server: string;
  result: string;
  nylasOptions: Record<NylasOption, string>;
  securityModes: Record<Security, string>;
  checks: Record<ProbeCheckId, string>;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value != null && !Array.isArray(value);

const str = (value: unknown): string | null =>
  typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;

const parseOutput = (output: unknown): Record<string, unknown> | null => {
  if (isRecord(output)) {
    return output;
  }
  if (typeof output !== 'string') {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(output);
    return isRecord(parsed) ? parsed : null;
  } catch {
    return null; // an error message from the tool, shown raw
  }
};

const oneOf = <T extends string>(
  value: unknown,
  options: Record<T, string>,
): T | null =>
  typeof value === 'string' && Object.hasOwn(options, value)
    ? (value as T) // narrowed by the hasOwn check on the label map's keys
    : null;

const recordText = (record: unknown): string => {
  if (typeof record === 'string') {
    return record;
  }
  if (isRecord(record)) {
    return Object.entries(record)
      .map(([key, value]) => `${key}=${String(value)}`)
      .join(' ');
  }
  return String(record);
};

const dnsResultRows = (
  results: Array<unknown>,
  isMx: boolean,
  labels: MailPresenterLabels,
): { rows: Array<Array<string>>; notes: Array<string> } => {
  const rows: Array<Array<string>> = [];
  const notes: Array<string> = [];
  for (const result of results) {
    if (!isRecord(result)) {
      continue;
    }
    const name = str(result.name) ?? '';
    const failure = str(result.error) ?? str(result.refused);
    if (failure != null) {
      rows.push(isMx ? [name, '', failure] : [name, failure]);
      continue;
    }
    if (isMx && result.acceptsMail === false) {
      notes.push(`${name}: ${labels.noMail}`);
    } else if (isMx && result.acceptsMail === 'implicit') {
      notes.push(`${name}: ${labels.implicitMx}`);
    }
    const records = Array.isArray(result.records) ? result.records : [];
    for (const record of records) {
      rows.push(
        isMx && isRecord(record)
          ? [name, String(record.priority ?? ''), str(record.exchange) ?? '.']
          : [name, recordText(record)],
      );
    }
  }
  return { rows, notes };
};

const presentDnsLookup = (
  input: Record<string, unknown>,
  output: unknown,
  labels: MailPresenterLabels,
): ToolPresentation => {
  const type = str(input.type) ?? '';
  const names = Array.isArray(input.names)
    ? input.names.filter((name): name is string => typeof name === 'string')
    : [];
  const parsed = parseOutput(output);
  const isMx = type === 'MX';
  const { rows, notes } = dnsResultRows(
    Array.isArray(parsed?.results) ? parsed.results : [],
    isMx,
    labels,
  );
  return {
    input: [
      {
        kind: 'rows',
        rows: [
          { label: labels.recordType, value: type },
          { label: labels.names, value: names.join(', ') },
        ],
      },
    ],
    output:
      parsed == null
        ? 'raw'
        : [
            {
              kind: 'table',
              columns: isMx
                ? [labels.domain, labels.priority, labels.mailServer]
                : [labels.domain, labels.value],
              rows,
            },
            ...notes.map((text): ReadableBlock => ({ kind: 'note', text })),
          ],
    complete: true,
  };
};

const serverRows = (
  title: string,
  settings: unknown,
  labels: MailPresenterLabels,
): Array<DetailRow> => {
  if (!isRecord(settings)) {
    return [];
  }
  const host = str(settings.host);
  const security = oneOf(settings.security, labels.securityModes);
  return [
    ...(host != null ? [{ label: title, value: host, copyable: true }] : []),
    {
      label: `${title} · ${labels.port}`,
      value: String(settings.port ?? ''),
      copyable: true,
    },
    ...(security != null
      ? [
          {
            label: `${title} · ${labels.security}`,
            value: labels.securityModes[security],
          },
        ]
      : []),
  ];
};

const mxTable = (
  mx: unknown,
  labels: MailPresenterLabels,
): Extract<ReadableBlock, { kind: 'table' }> => ({
  kind: 'table',
  title: labels.mailServer,
  columns: [labels.priority, labels.mailServer],
  rows: (Array.isArray(mx) ? mx : []).flatMap(record =>
    isRecord(record)
      ? [[String(record.priority ?? ''), str(record.exchange) ?? '.']]
      : [],
  ),
});

const presentMailSettingsDetect = (
  input: Record<string, unknown>,
  output: unknown,
  labels: MailPresenterLabels,
): ToolPresentation => {
  const target = str(input.emailOrDomain);
  const parsed = parseOutput(output);
  const inputBlocks: Array<ReadableBlock> =
    target != null
      ? [
          {
            kind: 'rows',
            rows: [{ label: labels.emailOrDomain, value: target }],
          },
        ]
      : [];
  if (parsed == null) {
    return { input: inputBlocks, output: 'raw', complete: true };
  }
  const option = oneOf(parsed.nylasOption, labels.nylasOptions);
  const provider = isRecord(parsed.provider) ? str(parsed.provider.name) : null;
  const filter = str(parsed.filter);
  const settings: Array<DetailRow> = [
    ...(option != null
      ? [{ label: labels.nylasOption, value: labels.nylasOptions[option] }]
      : []),
    ...(provider != null ? [{ label: labels.provider, value: provider }] : []),
    ...(filter != null ? [{ label: labels.mailFilter, value: filter }] : []),
    ...serverRows(labels.imapServer, parsed.imap, labels),
    ...serverRows(labels.smtpServer, parsed.smtp, labels),
  ];
  const notes = Array.isArray(parsed.notes)
    ? parsed.notes.flatMap((note): Array<ReadableBlock> => {
        const text = str(note);
        return text != null ? [{ kind: 'note', text }] : [];
      })
    : [];
  const mx = mxTable(parsed.mx, labels);
  return {
    input: inputBlocks,
    output: [
      ...(settings.length > 0
        ? [{ kind: 'rows' as const, rows: settings }]
        : []),
      ...notes,
      ...(mx.rows.length > 0 ? [mx] : []),
    ],
    complete: true,
  };
};

const presentMailServerProbe = (
  input: Record<string, unknown>,
  output: unknown,
  labels: MailPresenterLabels,
): ToolPresentation => {
  const host = str(input.host);
  const port = typeof input.port === 'number' ? input.port : null;
  const parsed = parseOutput(output);
  const checks = Array.isArray(parsed?.checks) ? parsed.checks : [];
  const items = checks.flatMap(check => {
    const id = isRecord(check) ? oneOf(check.check, labels.checks) : null;
    return isRecord(check) && id != null
      ? [
          {
            label: labels.checks[id],
            ok: check.ok === true,
            detail: str(check.detail) ?? '',
          },
        ]
      : [];
  });
  const outcome = str(parsed?.outcome);
  return {
    input:
      host != null
        ? [
            {
              kind: 'rows',
              rows: [
                {
                  label: labels.server,
                  value: port != null ? `${host}:${port}` : host,
                },
              ],
            },
          ]
        : [],
    output:
      items.length === 0
        ? 'raw'
        : [
            { kind: 'checks', items },
            ...(outcome != null
              ? [
                  {
                    kind: 'rows' as const,
                    rows: [{ label: labels.result, value: outcome }],
                  },
                ]
              : []),
          ],
    complete: true,
  };
};

export const presentMailTool = (
  toolName: string,
  input: Record<string, unknown>,
  output: unknown,
  labels: MailPresenterLabels,
): ToolPresentation | null => {
  switch (toolName) {
    case 'dns_lookup':
      return presentDnsLookup(input, output, labels);
    case 'mail_settings_detect':
      return presentMailSettingsDetect(input, output, labels);
    case 'mail_server_probe':
      return presentMailServerProbe(input, output, labels);
    default:
      return null;
  }
};
